//@flow

import {useEffect, useRef, useState, useCallback} from 'react';
import {useSelector} from 'react-redux';

import {
  getQuickRepliesService,
  createQuickReplyService,
  editQuickReplyService,
  deleteQuickReplyService,
} from './promises';
import {uploadImageService} from '../../file-storage/promises';
import {actions as QuickReplyActions} from './duck';
import {useActions} from '../../../utils/globalStore';

import type {RootState} from '../..';
import type {
  QuickReply,
  QuickReplyRequest,
  QuickReplyFileTypes,
} from '../../../entities';
import type {QuickReplyField} from './components/RepliesForm/constants';

const quickRepliesSelector = (state: RootState) =>
  state.quickReply.quickReplies;

const quickRepliesPaginationSelector = (state: RootState) =>
  state.quickReply.pagination;

export function useQuickReplies() {
  const quickReplies: QuickReply[] = useSelector(quickRepliesSelector);
  const quickRepliesPagination = useSelector(quickRepliesPaginationSelector);

  const setQuickReplies = useActions(QuickReplyActions.setQuickReplies);

  const [loading, setLoading] = useState(false);

  const triesCount = useRef(0);

  useEffect(() => {
    triesCount.current++;
    if (quickReplies.length > 0) {
      return;
    }
    if (triesCount.current > 3) {
      return;
    }
    setLoading(true);
    getQuickRepliesService()
      .then(({pagination, data}) => {
        setQuickReplies(data, pagination);
      })
      .catch(() => {
        setQuickReplies([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [quickReplies.length, setQuickReplies]);

  return {quickReplies, loading, pagination: quickRepliesPagination};
}

export function useLocalQuickReplies() {
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const triesCount = useRef(0);

  useEffect(() => {
    if (quickReplies.length > 0) {
      return;
    }
    if (triesCount.current > 3) {
      return;
    }
    setLoading(true);
    getQuickRepliesService({
      page: 0,
      size: 25,
    })
      .then(({pagination, data}) => {
        setQuickReplies(data);
        setPagination(pagination);
      })
      .catch(() => {
        setQuickReplies([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [quickReplies.length]);

  const searchQuickReplies = useCallback((search: string) => {
    if (search.length === 0) {
      setQuickReplies([]);
      return;
    }
    setLoading(true);
    getQuickRepliesService({
      page: 0,
      size: 8,
      name: search,
    })
      .then(({pagination, data}) => {
        setQuickReplies(data);
        setPagination(pagination);
      })
      .catch(() => {
        setQuickReplies([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return {loading, quickReplies, pagination, searchQuickReplies};
}

export function useQuickRepliesActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean | string>(false);

  const quickReplyPagination = useSelector(quickRepliesPaginationSelector);

  const quickReplyActions = useActions(QuickReplyActions);

  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => {
      setError(false);
    }, 4000);

    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  const uploadFile = async (
    file: File
  ): Promise<{
    type: QuickReplyFileTypes,
    id: string,
  }> => {
    console.log({
      file,
    });
    let fileType: QuickReplyFileTypes = 'FILE';
    if (file.type.includes('image')) {
      fileType = 'IMAGE';
    }

    if (file.type.includes('video')) {
      fileType = 'VIDEO';
    }

    if (file.type.includes('audio')) {
      fileType = 'AUDIO';
    }

    const [fileUploaded] = await uploadImageService(file, 'im/quick-reply');
    return {
      type: fileType,
      id: fileUploaded.id,
    };
  };

  const createQuickReply = async (quickReply: QuickReplyField) => {
    try {
      setLoading(true);
      const {files, message, name, filename} = quickReply;
      let fileType: QuickReplyFileTypes;
      let fileId: string;

      if (files) {
        const {id, type} = await uploadFile(files);

        fileType = type;
        fileId = id;
      }

      const payload: QuickReplyRequest = {
        message,
        name,
        fileId,
        fileName: filename,
        fileType,
      };

      const quickReplyCreated = await createQuickReplyService(payload);
      quickReplyActions.addQuickReply(quickReplyCreated);
      return quickReplyCreated;
    } catch (error) {
      setError('Error al crear la respuesta rápida');
    } finally {
      setLoading(false);
    }
  };

  const editQuickReply = async (id: string, quickReply: QuickReplyField) => {
    setLoading(true);
    try {
      const {files, message, name, filename} = quickReply;

      let fileType: QuickReplyFileTypes;
      let fileId = quickReply?.fileId || undefined;

      if (files) {
        const {id, type} = await uploadFile(files);

        fileType = type;
        fileId = id;
      }

      const payload: QuickReplyRequest = {
        message,
        name,
        fileId,
        fileType,
        fileName: filename,
      };

      const quickReplyEdited = await editQuickReplyService(id, payload);

      quickReplyActions.editQuickReply(quickReplyEdited);
    } catch (error) {
      setError('Error al editar la respuesta rápida');
    } finally {
      setLoading(false);
    }
  };

  const deleteQuickReply = async (id: string) => {
    setLoading(true);

    try {
      await deleteQuickReplyService(id);
      quickReplyActions.deleteQuickReply(id);
    } catch (error) {
      setError('Error al eliminar la respuesta rápida');
    } finally {
      setLoading(false);
    }
  };

  const changeQuickReplyPagination = async (page: number) => {
    setLoading(true);
    try {
      const {data, pagination} = await getQuickRepliesService({
        ...quickReplyPagination,
        page: page - 1,
      });
      quickReplyActions.setQuickReplies(data, pagination);
    } catch (error) {
      setError('Error al obtener las respuestas rápidas');
    } finally {
      setLoading(false);
    }
  };

  return {
    error,
    loading,
    editQuickReply,
    deleteQuickReply,
    createQuickReply,
    changeQuickReplyPagination,
  };
}
